'use client';

import { useRef, useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { RotateCcw, AlertTriangle, ZoomIn, ZoomOut, Pencil, MessageSquarePlus, Layers, Palette, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { BuilderSidebar } from './BuilderSidebar';
import { ExportButtons } from './ExportButtons';
import { SaveTemplateButton } from './SaveTemplateButton';
import { FeedbackModal } from './FeedbackModal';
import { ReceiptCanvas } from '@/components/receipt';
import { WatermarkRequiredContext } from '@/components/receipt/watermark-context';
import { useUpgrade } from '@/components/upgrade/UpgradeModal';
import { Sparkles } from 'lucide-react';
import { useAuth } from '@/components/auth/auth-provider';
import { useReceiptBuilder } from '@/hooks/useReceiptBuilder';
import { getTemplateById } from '@/lib/templates/brands';
import { cn } from '@/lib/utils';

const ZOOM_LEVELS = [0.5, 0.75, 1, 1.25, 1.5, 2];
const DEFAULT_ZOOM = 1;

export interface BuilderPageProps {
  brandName?: string;
  brandLogo?: string;
  brandSlug?: string;
}

/** Sits above the receipt preview on the free plan and opens the Pro upsell. */
function WatermarkUpsellChip() {
  const { open } = useUpgrade();
  return (
    <button
      type="button"
      onClick={() => open()}
      className="inline-flex items-center gap-1.5 rounded-full border border-[#7fbdf2] bg-[#E7F5FF] px-3.5 py-1.5 text-xs font-semibold text-[#0b4f92] shadow-sm transition-all hover:bg-[#d8ecff] hover:border-[#0068D6] hover:shadow-md hover:-translate-y-px active:translate-y-0"
    >
      <Sparkles className="h-3.5 w-3.5 text-[#0068D6]" />
      Remove watermark
    </button>
  );
}

export function BuilderPage({ brandName, brandLogo, brandSlug }: BuilderPageProps) {
  const { isPaid } = useAuth();
  const receiptRef = useRef<HTMLDivElement>(null);
  const { template, resetTemplate, setTemplate, updateSection, selectSection, lastAppliedBrand, setLastAppliedBrand } = useReceiptBuilder();
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  const [mobileView, setMobileView] = useState<'edit' | 'preview'>('edit');
  const [sidebarTab, setSidebarTab] = useState('content');
  const [hasInitialized, setHasInitialized] = useState(false);
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setPortalTarget(document.getElementById('builder-subheader-portal'));
  }, []);

  // Entering Preview must always show the receipt from the top. On iOS, an
  // input focused in Edit shifts the visual viewport (even with body overflow
  // locked); that offset survives the tab switch and makes the top of the
  // receipt unreachable by scrolling.
  const previewScrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (mobileView !== 'preview') return;
    const active = document.activeElement;
    if (active instanceof HTMLElement) active.blur();
    window.scrollTo(0, 0);
    if (previewScrollRef.current) previewScrollRef.current.scrollTop = 0;
  }, [mobileView]);

  // Load brand template when component mounts.
  // Guarded by the persisted lastAppliedBrand: a login redirect (or any
  // reload) remounts this page, and re-applying the preset then would wipe
  // the user's in-progress work. The preset applies only when this brand
  // page is a fresh entry context, not a remount of the same one.
  useEffect(() => {
    if (hasInitialized) return;

    const brandKey = brandSlug || brandName || null;
    if (brandKey && lastAppliedBrand === brandKey) {
      setHasInitialized(true);
      return;
    }

    // Try to load the brand-specific template
    if (brandSlug) {
      const brandTemplate = getTemplateById(brandSlug);
      if (brandTemplate) {
        setTemplate(brandTemplate);
        setLastAppliedBrand(brandKey!);
        setHasInitialized(true);
        return;
      }
    }

    // Fallback: Find the header section and update with brand data
    const headerSection = template.sections.find(s => s.type === 'header');
    if (headerSection && headerSection.type === 'header') {
      if (brandLogo) {
        // If brand has logo, use logo and clear business name
        updateSection(headerSection.id, {
          ...headerSection.config,
          logoUrl: brandLogo,
          businessName: '', // Leave empty when logo is available
        });
      } else if (brandName) {
        // If no logo but has brand name, use brand name as business name
        updateSection(headerSection.id, {
          ...headerSection.config,
          businessName: brandName,
        });
      }
      if (brandKey && (brandLogo || brandName)) setLastAppliedBrand(brandKey);
    }

    setHasInitialized(true);
  }, [brandName, brandLogo, brandSlug, template.sections, setTemplate, updateSection, hasInitialized, lastAppliedBrand, setLastAppliedBrand]);

  const handleResetConfirm = () => {
    resetTemplate();
    setShowResetDialog(false);
  };

  const zoomIn = () => {
    const currentIndex = ZOOM_LEVELS.indexOf(zoom);
    if (currentIndex < ZOOM_LEVELS.length - 1) {
      setZoom(ZOOM_LEVELS[currentIndex + 1]);
    }
  };

  const zoomOut = () => {
    const currentIndex = ZOOM_LEVELS.indexOf(zoom);
    if (currentIndex > 0) {
      setZoom(ZOOM_LEVELS[currentIndex - 1]);
    }
  };

  const resetZoom = () => {
    setZoom(DEFAULT_ZOOM);
  };

  // Handle clicking on a receipt section in the canvas preview
  const handleCanvasClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    const sectionEl = target.closest('[data-section-id]');
    if (sectionEl) {
      const sectionId = sectionEl.getAttribute('data-section-id');
      if (sectionId) {
        selectSection(sectionId);
        // On mobile, switch to edit view so the sidebar is visible
        setMobileView('edit');
      }
    }
  }, [selectSection]);

  // Use brand name for template name if available
  const templateName = brandName ? `${brandName} Receipt` : template.name;

  return (
    <div className="flex-1 flex builder-container bg-muted/30 overflow-hidden border-t border-border overscroll-none">
      {/* Reset Confirmation Dialog */}
      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Reset Receipt?
            </DialogTitle>
            <DialogDescription className="text-left pt-2">
              This will reset all your inputs and data to the default template values. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowResetDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleResetConfirm}>
              Reset All
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sidebar - hidden on mobile when preview is active */}
      <aside className={cn(
        "builder-sidebar builder-sidebar-width shrink-0 border-r overflow-hidden overscroll-contain",
        mobileView !== 'edit' && "builder-hide-on-preview"
      )}>
        <BuilderSidebar activeTab={sidebarTab} onTabChange={setSidebarTab} />
      </aside>

      {/* Builder controls — portaled into the header row on mobile */}
      {portalTarget && createPortal(
        mobileView === 'edit' ? (
          <div className="flex items-center rounded-md border overflow-hidden">
            <button
              onClick={() => setSidebarTab('content')}
              className={cn(
                "flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors",
                sidebarTab === 'content'
                  ? "bg-[#0068D6] text-white"
                  : "bg-white text-gray-600"
              )}
            >
              <Layers className="h-3.5 w-3.5" />
              Content
            </button>
            <button
              onClick={() => setSidebarTab('design')}
              className={cn(
                "flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors border-l",
                sidebarTab === 'design'
                  ? "bg-[#0068D6] text-white"
                  : "bg-white text-gray-600"
              )}
            >
              <Palette className="h-3.5 w-3.5" />
              Design
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-1.5">
            <Button variant="outline" size="sm" onClick={() => setShowResetDialog(true)} className="h-8 w-8 p-0">
              <RotateCcw className="h-3.5 w-3.5" />
            </Button>
            <SaveTemplateButton templateName={templateName} compact />
            <ExportButtons receiptRef={receiptRef} templateName={templateName} templateSlug={brandSlug} compact />
          </div>
        ),
        portalTarget
      )}

      {/* Preview Area - hidden on mobile when edit is active */}
      <main className={cn(
        "builder-main flex-1 bg-muted/50 relative overflow-hidden overscroll-contain",
        mobileView !== 'preview' && "builder-hide-on-edit"
      )}>
        {/* Mobile: fixed above the Edit/Preview tab bar, outside the scroll
            flow entirely, so no scroll position can hide it. Vanishes with the
            whole pane when the Edit tab is active (display:none ancestor). */}
        {!isPaid && (
          <div className="lg:hidden fixed bottom-16 left-0 right-0 z-30 flex justify-center pointer-events-none pb-[env(safe-area-inset-bottom)]">
            <span className="pointer-events-auto">
              <WatermarkUpsellChip />
            </span>
          </div>
        )}
        {/* Desktop Left Toolbar - Reset & Zoom */}
        <div className="builder-desktop-toolbar absolute top-4 left-4 flex-col gap-2 z-10 w-36">
          <Button variant="outline" size="sm" onClick={() => setShowResetDialog(true)} className="h-10 w-full justify-center bg-background hover:bg-muted shadow-sm">
            <RotateCcw className="h-4 w-4 mr-2" />
            <span className="text-xs">Reset</span>
          </Button>

          <SaveTemplateButton templateName={templateName} />
          
          {/* Zoom Controls */}
          <div className="flex bg-background rounded-md border shadow-sm overflow-hidden w-full">
            <button
              onClick={zoomOut}
              disabled={zoom === ZOOM_LEVELS[0]}
              className="flex-1 h-10 flex items-center justify-center hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Zoom Out"
            >
              <ZoomOut className="h-4 w-4" />
            </button>
            <button
              onClick={resetZoom}
              className="flex-1 h-10 flex items-center justify-center hover:bg-muted border-x transition-colors text-xs font-medium"
              title="Reset Zoom"
            >
              {Math.round(zoom * 100)}%
            </button>
            <button
              onClick={zoomIn}
              disabled={zoom === ZOOM_LEVELS[ZOOM_LEVELS.length - 1]}
              className="flex-1 h-10 flex items-center justify-center hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Zoom In"
            >
              <ZoomIn className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Desktop Bottom-Left - Feedback */}
        <div className="builder-desktop-toolbar absolute bottom-4 left-4 z-10">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFeedback(true)}
            className="h-10 bg-background hover:bg-muted shadow-sm gap-2"
          >
            <MessageSquarePlus className="h-4 w-4" />
            <span className="text-xs">Feedback</span>
          </Button>
        </div>

        {/* Desktop Right Toolbar - Download */}
        <div className="builder-desktop-toolbar absolute top-4 right-4 z-10">
          <ExportButtons receiptRef={receiptRef} templateName={templateName} templateSlug={brandSlug} position="right" />
        </div>
        
        {/* Receipt Preview - Scrollable Area */}
        <div ref={previewScrollRef} className="h-full overflow-auto overscroll-contain touch-pan-y builder-mobile-preview-scroll">
          <div className="flex items-start justify-center builder-preview-padding min-h-full">
            {/* Zoom wrapper - desktop only zoom, mobile fixed at 100% */}
            <div 
              className="transition-transform duration-200 builder-mobile-no-zoom"
              style={{ 
                transform: `scale(${zoom})`, 
                transformOrigin: 'top center' 
              }}
            >
              <div className="bg-muted px-4 pt-2 pb-4 lg:px-8 lg:pt-3 lg:pb-8 rounded-xl" onClick={handleCanvasClick}>
                {/* Free plan: watermark is forced on the preview AND the captured
                    export. The chip sits OUTSIDE receiptRef, so it never lands
                    in the exported image. */}
                {/* Desktop only: on mobile the chip is a fixed pill above the
                    tab bar instead (see below) - iOS Safari does not reliably
                    engage position:sticky inside overflow:auto touch scrollers,
                    which made the first sticky fix pass in emulation and fail
                    on a real iPhone. */}
                {!isPaid && (
                  <div className="hidden lg:flex justify-center mb-3">
                    <WatermarkUpsellChip />
                  </div>
                )}
                <WatermarkRequiredContext.Provider value={!isPaid}>
                  <ReceiptCanvas ref={receiptRef} template={template} />
                </WatermarkRequiredContext.Provider>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Mobile Tab Bar */}
      <div className="builder-mobile-bar fixed bottom-0 left-0 right-0 bg-white border-t z-20 safe-area-bottom touch-none shadow-[0_-1px_3px_rgba(0,0,0,0.05)]">
        <button 
          onClick={() => setMobileView('edit')}
          className={cn(
            "flex-1 py-2.5 px-4 flex flex-row items-center justify-center gap-2 transition-colors",
            mobileView === 'edit' 
              ? "bg-white text-gray-900 font-medium" 
              : "bg-gray-50 text-gray-500"
          )}
        >
          <Pencil className="h-4 w-4" />
          <span className="text-sm">Edit</span>
        </button>
        <button 
          onClick={() => setMobileView('preview')}
          className={cn(
            "flex-1 py-2.5 px-4 flex flex-row items-center justify-center gap-2 transition-colors",
            mobileView === 'preview' 
              ? "bg-white text-gray-900 font-medium" 
              : "bg-gray-50 text-gray-500"
          )}
        >
          {/* Small receipt icon */}
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z" />
            <path d="M8 7h8" />
            <path d="M8 11h8" />
            <path d="M8 15h4" />
          </svg>
          <span className="text-sm">Preview</span>
        </button>
      </div>

      {/* Feedback Modal (desktop) */}
      <FeedbackModal open={showFeedback} onOpenChange={setShowFeedback} />
    </div>
  );
}

export default BuilderPage;

